import React, { useEffect, useState } from 'react'
import {
  Globe, Loader2, Search, History as HistoryIcon, Trash2, FolderInput,
  ExternalLink, Clapperboard, CheckCircle2,
} from 'lucide-react'
import { ChannelResults } from './ChannelDiscovery'
import { useProjects } from '../hooks/useRag'
import {
  useDiscoverChannels, useDiscoverItems, useDiscoveryHistory, useDiscoveryRun, useDeleteDiscoveryRun,
} from '../hooks/useTranscripts'
import { useSubmitImport } from '../hooks/useImport'
import { useImportFeedItem } from '../hooks/useFeeds'
import { canEmbed } from './VideoPlayer'
import usePlayerStore from '../stores/playerStore'

// F3.2 — Research → Discover: one query fans out to channels (AI shortlist,
// weighted) AND items (videos + subscribed feed items), each with a one-click
// action into the selected project. Seeded from Topics via seedQuery/seedNonce.
// F3.3 — history: mined runs reopen instantly from the control plane.

const fmtDur = (s) => {
  if (!s && s !== 0) return ''
  const m = Math.floor(s / 60)
  return `${m}:${String(Math.round(s % 60)).padStart(2, '0')}`
}
const fmtK = (n) =>
  n == null ? null : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}k` : String(n)

export default function DiscoverPanel({ seedQuery = '', seedNonce = 0 }) {
  const [q, setQ] = useState('')
  const [project, setProject] = useState('')
  const projectsQuery = useProjects()
  const channels = useDiscoverChannels()
  const items = useDiscoverItems()
  const historyQ = useDiscoveryHistory()
  const openRun = useDiscoveryRun()
  const delRun = useDeleteDiscoveryRun()

  const run = (query) => {
    const text = (query ?? q).trim()
    if (!text) return
    setQ(text)
    openRun.reset()
    channels.mutate({ query: text, n: 5, project: project || undefined })
    items.mutate({ query: text, n: 6 })
  }

  // Seed from Topics ("Find sources" on a gap-analysis result).
  useEffect(() => {
    if (seedNonce && seedQuery) run(seedQuery)
  }, [seedNonce]) // eslint-disable-line react-hooks/exhaustive-deps

  // A reopened history run replaces live channel results.
  const channelData = openRun.data ?? channels.data
  const busy = channels.isPending || items.isPending

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={project}
          onChange={(e) => setProject(e.target.value)}
          className="h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          title="Target project for imports"
        >
          <option value="">Target project…</option>
          {(projectsQuery.data?.projects ?? []).map((p) => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[260px]">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft dark:text-mist/50" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && run()}
            placeholder="What are you missing? e.g. energy protection practices comparison"
            className="w-full h-10 rounded-lg pl-9 pr-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          />
        </div>
        <button
          onClick={() => run()}
          disabled={busy || !q.trim()}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Discover
        </button>
      </div>

      {busy && (
        <p className="text-sm text-ink-soft dark:text-mist/60">
          Searching YouTube, Rutube, podcast directories and your feeds — channels are weighted by
          popularity and consolidated by AI (~30–60 s)…
        </p>
      )}
      {channels.isError && <p className="text-sm text-red-600">{channels.error?.message}</p>}

      {/* item-level results: videos + subscribed feed items */}
      {items.data && <ItemResults data={items.data} project={project} />}

      {/* channel-level results (or a reopened history run) */}
      {channelData && <ChannelResults data={channelData} />}

      {/* F3.3 — history of mined runs */}
      {(historyQ.data?.history?.length ?? 0) > 0 && (
        <section className="space-y-1.5">
          <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold flex items-center gap-1.5">
            <HistoryIcon className="w-3.5 h-3.5" /> Discovery history
          </h3>
          {historyQ.data.history.map((h) => (
            <div key={h.id} className="flex items-center gap-2 text-sm">
              <button
                onClick={() => { setQ(h.query); openRun.mutate(h.id) }}
                className="flex-1 min-w-0 text-left truncate rounded-lg border border-parchment-border bg-white/50 px-3 py-1.5 hover:border-gold text-ink dark:text-mist dark:border-midnight-600 dark:bg-midnight-800/40"
                title="Reopen this run's results (no re-search)"
              >
                {h.query}
                <span className="ml-2 text-xs text-ink-soft dark:text-mist/50">
                  {new Date(h.created_at).toLocaleString()}
                </span>
              </button>
              <button
                onClick={() => delRun.mutate(h.id)}
                className="p-1.5 rounded text-ink-soft hover:text-red-600 dark:text-mist/50"
                aria-label="Delete run"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

function ItemResults({ data, project }) {
  const groups = [
    ['youtube', data.results?.youtube ?? []],
    ['rutube', data.results?.rutube ?? []],
    ['feed', data.results?.feed ?? []],
  ].filter(([, list]) => list.length)
  if (!groups.length) return null
  return (
    <div className="space-y-2">
      {groups.map(([src, list]) => (
        <div key={src} className="space-y-1">
          <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50">
            {src === 'feed' ? 'your feeds' : `${src} videos`}
          </div>
          {list.map((v, i) => (
            <ItemRow key={`${src}${i}`} item={v} project={project} />
          ))}
        </div>
      ))}
    </div>
  )
}

function ItemRow({ item, project }) {
  const submit = useSubmitImport()
  const importFeed = useImportFeedItem()
  const openPlayer = usePlayerStore((s) => s.openPlayer)
  const isFeed = item.source === 'feed'
  const playable = !isFeed && canEmbed({ source_url: item.url, origin: item.source, video_id: item.id })
  const done = submit.isSuccess || importFeed.isSuccess || item.imported_to

  const doImport = () => {
    if (isFeed) importFeed.mutate({ itemId: Number(item.id), project })
    else submit.mutate({ input: item.url, source: item.source, project })
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-parchment-border bg-white/50 px-3 py-2 dark:border-midnight-600 dark:bg-midnight-800/40">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-ink dark:text-mist truncate" title={item.title}>{item.title}</div>
        <div className="text-xs text-ink-soft dark:text-mist/50 truncate flex items-center gap-2">
          {item.channel && <span className="truncate">{item.channel}</span>}
          {item.duration != null && <span>{fmtDur(item.duration)}</span>}
          {fmtK(item.views) && <span>👁 {fmtK(item.views)}</span>}
          {playable && (
            <button
              onClick={() => openPlayer({ source_url: item.url, origin: item.source, video_id: item.id, title: item.title, start: 0 })}
              className="inline-flex items-center gap-0.5 hover:text-gold-deep dark:hover:text-gold"
            >
              <Clapperboard className="w-3 h-3" /> watch
            </button>
          )}
          {item.url && !playable && (
            <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:text-gold-deep dark:hover:text-gold">
              <ExternalLink className="w-3 h-3" /> open
            </a>
          )}
        </div>
      </div>
      {done ? (
        <span className="shrink-0 inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="w-3.5 h-3.5" /> {item.imported_to ? `in ${item.imported_to}` : 'importing…'}
        </span>
      ) : (
        <button
          onClick={doImport}
          disabled={!project || submit.isPending || importFeed.isPending}
          title={project ? `Transcribe & index into "${project}" (server-side saga)` : 'Pick a target project first'}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
        >
          {submit.isPending || importFeed.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <FolderInput className="w-3.5 h-3.5" />
          )}
          Import
        </button>
      )}
    </div>
  )
}
